import { yup } from '@framework/validation/initYup.ts';
import { type YupArraySchema } from '@framework/validation/YupArraySchema.ts';
import { type Maybe } from 'yup';
import { RichTextAdvancedSchema } from '../../../../../inputs/rich-text-advanced/schema/RichTextAdvancedSchema.ts';
import { CommonBaseWidgetSchema } from '../../../CommonBaseWidgetSchema.ts';
import { LazyWidgetSchema } from '../../../LazyWidgetSchema.ts';
import { uniqueWidgetCodeSchema } from '../../../validation/uniqueWidgetCodeSchema.ts';
import { type Widget, WidgetType } from '../../../Widget.ts';
import { BaseLayoutSchema } from '../../common/BaseLayoutSchema.ts';
import {
  type ITableColumnWidget,
  type ITableInputWidget,
} from './ITableInputWidget.ts';
import { type ITableRow, TableRowSchema } from './ITableRow.ts';

export const TableInputConfigSchema: yup.ObjectSchema<ITableInputWidget> =
  CommonBaseWidgetSchema.shape({
    label: RichTextAdvancedSchema.label('Etichetta'),
    type: yup.string().exact(WidgetType.TableInput).required(),
    code: uniqueWidgetCodeSchema,
    helpText: RichTextAdvancedSchema.notRequired().label('Descrizione'),
    columns: yup
      .array(LazyWidgetSchema)
      .required()
      .default([]) as YupArraySchema<ITableColumnWidget>,
    rows: yup
      .array(TableRowSchema)
      .required()
      .default([])
      .test(
        'is-unique',
        'Sono presenti dei codici di riga non univoci.',
        checkRowUniqueCode,
      )
      .default([]) as YupArraySchema<ITableRow>,

    /**
     * Table è un widget speciale in cui il campo children non è utilizzato in configurazione
     * ma solo in preview e in fase di compilazione con la materializedTree.
     */
    children: yup.array().notRequired().default([]) as YupArraySchema<Widget>,

    // Layout
    layout: BaseLayoutSchema.shape({
      hideTableHeader: yup
        .boolean()
        .required()
        .default(false)
        .label('Nascondi Header della tabella'),
      rowsLabelColumnWidth: yup
        .number()
        .transform(t => (t === null ? undefined : t))
        .optional()
        .max(100)
        .min(5)
        .label('Larghezza colonna etichetta righe (%)'),
      lightTableHeader: yup
        .boolean()
        .required()
        .default(false)
        .label('Header tabella con sfondo bianco'),
      showTableTitle: yup
        .boolean()
        .required()
        .default(false)
        .label('Mostra titolo della tabella'),
      tableTitle: yup.string().optional().default('').label('Titolo Tabella'),
    }),
  });

/**
 * Controlla che tutti i codici delle righe della tabella siano univoci.
 * In caso contrario, crea un errore sul campo rows.
 */
function checkRowUniqueCode(
  value: Maybe<{ code?: string | null | undefined }[]>,
) {
  if (!value) return true;

  for (const row of value) {
    if (value.filter(r => r.code === row.code).length > 1) {
      return false;
    }
  }

  return true;
}
